import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown, ChevronRight, Plus, Folder as FolderIcon,
  List as ListIcon, Settings, LogOut, Building2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { initials } from "@/lib/format";
import CreateWorkspaceDialog from "@/components/dialogs/CreateWorkspaceDialog";
import CreateFolderDialog from "@/components/dialogs/CreateFolderDialog";
import CreateListDialog from "@/components/dialogs/CreateListDialog";
import NotificationsBell from "@/components/NotificationsBell";
import { toast } from "sonner";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { workspaces, activeWorkspace, activeWorkspaceId, switchWorkspace, tree, refreshTree } = useWorkspace();
  const [expanded, setExpanded] = useState({});
  const [openWorkspace, setOpenWorkspace] = useState(false);
  const [openFolder, setOpenFolder] = useState(false);
  const [openList, setOpenList] = useState({ open: false, folderId: null });
  const location = useLocation();
  const navigate = useNavigate();

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const handleDeleteList = async (listId) => {
    if (!window.confirm("Delete this list and all its tasks?")) return;
    try {
      await api.delete(`/lists/${listId}`);
      toast.success("List deleted");
      await refreshTree();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <aside className="w-64 fixed left-0 top-0 bottom-0 border-r border-neutral-200 bg-neutral-100 flex flex-col z-30" data-testid="sidebar">
      {/* Brand + workspace switcher */}
      <div className="px-3 pt-4 pb-3 border-b border-neutral-200">
        <div className="font-display text-lg font-black tracking-tight flex items-center gap-2 mb-3 px-1">
          <span className="inline-block w-2.5 h-2.5 bg-yellow-400" /> ORBIT
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center justify-between gap-2 px-2 py-2 bg-white border border-neutral-200 rounded-sm hover:bg-neutral-50 text-left transition-colors"
              data-testid="workspace-switcher-button"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 bg-neutral-950 text-white text-[10px] font-bold flex items-center justify-center rounded-sm">
                  {activeWorkspace ? initials(activeWorkspace.name) : "—"}
                </div>
                <span className="text-sm font-semibold truncate">
                  {activeWorkspace ? activeWorkspace.name : "No workspace"}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-neutral-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <DropdownMenuLabel className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Workspaces</DropdownMenuLabel>
            {workspaces.map((w) => (
              <DropdownMenuItem
                key={w.id}
                onSelect={() => { switchWorkspace(w.id); navigate("/"); }}
                data-testid={`workspace-option-${w.id}`}
              >
                <Building2 className="w-3.5 h-3.5 mr-2" /> {w.name}
                <span className="ml-auto font-mono-data text-[10px] text-neutral-400 uppercase">{w.my_role}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setOpenWorkspace(true)} data-testid="create-workspace-trigger">
              <Plus className="w-3.5 h-3.5 mr-2" /> New workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto px-2 py-3">
        <div className="flex items-center justify-between px-2 mb-1">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Folders</div>
          {activeWorkspace && ["admin", "member"].includes(activeWorkspace.my_role) && (
            <button
              onClick={() => setOpenFolder(true)}
              className="text-neutral-500 hover:text-neutral-950 transition-colors"
              data-testid="create-folder-trigger"
              title="New folder"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {tree.folders.length === 0 && (
          <div className="px-2 py-6 text-center" data-testid="empty-folders">
            <div className="text-xs text-neutral-500">No folders yet.</div>
            {activeWorkspace && ["admin", "member"].includes(activeWorkspace.my_role) && (
              <button
                onClick={() => setOpenFolder(true)}
                className="mt-2 text-xs font-semibold text-blue-700 hover:underline"
                data-testid="empty-folders-create-button"
              >
                + Create your first folder
              </button>
            )}
          </div>
        )}

        {tree.folders.map((f) => {
          const lists = tree.listsByFolder[f.id] || [];
          const isOpen = expanded[f.id] ?? true;
          return (
            <div key={f.id} className="mb-1">
              <div className="flex items-center group rounded-sm hover:bg-white">
                <button
                  onClick={() => toggle(f.id)}
                  className="flex items-center gap-1 flex-1 px-2 py-1.5 text-sm"
                  data-testid={`folder-toggle-${f.id}`}
                >
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-neutral-500" /> : <ChevronRight className="w-3.5 h-3.5 text-neutral-500" />}
                  <FolderIcon className="w-3.5 h-3.5 text-neutral-700" />
                  <span className="font-medium truncate">{f.name}</span>
                </button>
                {activeWorkspace && ["admin", "member"].includes(activeWorkspace.my_role) && (
                  <button
                    onClick={() => setOpenList({ open: true, folderId: f.id })}
                    className="opacity-0 group-hover:opacity-100 px-2 text-neutral-500 hover:text-neutral-950 transition-opacity"
                    title="New list"
                    data-testid={`create-list-trigger-${f.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="ml-5 border-l border-neutral-200">
                  {lists.length === 0 && (
                    <div className="pl-3 py-1.5 text-xs text-neutral-400">No lists</div>
                  )}
                  {lists.map((l) => {
                    const isActive = location.pathname === `/list/${l.id}`;
                    return (
                      <div key={l.id} className="flex items-center group">
                        <Link
                          to={`/list/${l.id}`}
                          className={`flex-1 flex items-center gap-2 pl-3 pr-2 py-1.5 text-sm rounded-sm transition-colors ${
                            isActive ? "bg-neutral-950 text-white" : "hover:bg-white text-neutral-800"
                          }`}
                          data-testid={`sidebar-list-${l.id}`}
                        >
                          <ListIcon className="w-3.5 h-3.5" />
                          <span className="truncate">{l.name}</span>
                        </Link>
                        {activeWorkspace && activeWorkspace.my_role === "admin" && (
                          <button
                            onClick={() => handleDeleteList(l.id)}
                            className="opacity-0 group-hover:opacity-100 px-2 text-neutral-400 hover:text-red-600"
                            title="Delete list"
                            data-testid={`delete-list-${l.id}`}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="border-t border-neutral-200 p-3 space-y-1 bg-white">
        <NotificationsBell />
        {activeWorkspace && (
          <Link
            to="/settings"
            className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-neutral-100 transition-colors"
            data-testid="nav-settings"
          >
            <Settings className="w-3.5 h-3.5" /> Workspace settings
          </Link>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-neutral-100 text-left transition-colors" data-testid="user-menu-button">
              <div className="w-7 h-7 bg-neutral-950 text-white text-[10px] font-bold flex items-center justify-center rounded-sm">
                {initials(user?.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{user?.name}</div>
                <div className="text-[11px] text-neutral-500 truncate">{user?.email}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onSelect={logout} data-testid="logout-button">
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CreateWorkspaceDialog open={openWorkspace} onOpenChange={setOpenWorkspace} />
      <CreateFolderDialog open={openFolder} onOpenChange={setOpenFolder} />
      <CreateListDialog
        open={openList.open}
        folderId={openList.folderId}
        onOpenChange={(v) => setOpenList((s) => ({ ...s, open: v }))}
      />
    </aside>
  );
}
